<template>
  <div class="mx-work" :class="[showList ? '' : 'is-nolist', railClass, selected ? 'is-open' : '']">

    <!-- ══ LISTA ══ -->
    <section v-if="showList" class="mx-convs">
      <div class="mx-glass"></div>
      <div class="mx-convs__b" ref="listScroll" @scroll="onListScroll">
        <div v-if="loadingList && !rows.length" class="mx-empty">Cargando conversaciones…</div>
        <div v-else-if="!rows.length" class="mx-empty">Sin resultados para los filtros aplicados.</div>
        <button
          v-for="c in rows"
          :key="c.id"
          type="button"
          class="mx-cv"
          :class="{ 'is-on': selected && selected.id === c.id }"
          @click="open(c)"
        >
          <span class="mx-cv__av">{{ ini(c.client_name || c.client_identifier) }}</span>
          <span class="mx-cv__l1">
            <span class="mx-cv__nm">{{ c.client_name || convPhone(c) || 'Sin nombre' }}</span>
          </span>
          <span class="mx-cv__l2">{{ c.last_message || convPhone(c) || 'Sin mensajes' }}</span>
          <span class="mx-cv__r">
            <span class="mx-cv__t" :class="{ 'is-risk': riskOf(c, false) === 'risk' }">{{ waitLabel(c) }}</span>
          </span>
        </button>
        <div v-if="loadingList && rows.length" class="mx-col__e">Cargando más…</div>
      </div>
    </section>

    <!-- ══ HILO ══ -->
    <section class="mx-wa">
      <div v-if="!selected" class="mx-empty" style="margin:auto">Elegí una conversación de la lista.</div>

      <template v-else>
        <header class="mx-wa__h">
          <!-- Cuando la lista y el hilo no caben juntos, esta flecha es la única
               forma de volver: el ⟨ de la barra sale del chat entero, al tablero. -->
          <button type="button" class="mx-act mx-wa__back" title="Volver a la lista" aria-label="Volver a la lista" @click="unopen">⟨</button>
          <span class="mx-wa__av">{{ ini(selected.client_name || selected.client_identifier) }}</span>
          <span class="mx-wa__id">
            <!-- El nombre se edita acá mismo, como en el dashboard viejo: el
                 lead llega con el número como nombre y el broker lo bautiza
                 mientras habla. Enter guarda, Esc cancela. -->
            <span class="mx-wa__nmrow">
              <template v-if="!editingName">
                <span class="mx-wa__nm">{{ selected.client_name || convPhone(selected) || 'Sin nombre' }}</span>
                <button type="button" class="mx-wa__pen" title="Editar el nombre" aria-label="Editar el nombre" @click="startEditName">✎</button>
              </template>
              <template v-else>
                <input
                  ref="nameInput"
                  class="mx-wa__nmin"
                  v-model="nameEdit"
                  maxlength="120"
                  placeholder="Nombre del lead"
                  @keydown.enter.prevent="saveName"
                  @keydown.esc="cancelEditName"
                />
                <button type="button" class="mx-wa__pen is-ok" title="Guardar" aria-label="Guardar" @click="saveName">✓</button>
                <button type="button" class="mx-wa__pen" title="Cancelar" aria-label="Cancelar" @click="cancelEditName">✕</button>
              </template>
            </span>
            <span class="mx-wa__st" :class="{ 'is-live': waitSecs(selected) != null && waitSecs(selected) < 3600 }">
              {{ presence }} · {{ convPhone(selected) }}
            </span>
          </span>
          <!-- Bot: el mismo interruptor que tenía el dashboard viejo. Se ve solo
               con la conversación abierta, porque en una cerrada el bot no
               contesta y prender/apagar no significa nada. En angosto queda el
               🤖 solo: el texto se esconde por CSS para no empujar al ▤. -->
          <button
            v-if="isSuperAdmin && selected.status !== 'closed'"
            type="button"
            class="mx-act mx-wa__bot"
            :class="{ 'is-on': botOn }"
            :disabled="botLoading"
            :title="botOn ? 'Desactivar el bot en esta conversación' : 'Activar el bot en esta conversación'"
            @click="toggleBot"
          >
            🤖<span class="mx-wa__botx">{{ botOn ? 'Bot activo' : 'Activar bot' }}</span>
          </button>
          <!-- Memoria del bot: la borra n8n, no nosotros. Se esconde en las
               cerradas por el mismo motivo que el interruptor de arriba, y solo
               la ve un super admin: borrar el contexto le cambia el hilo al
               cliente y no se puede deshacer. -->
          <button
            v-if="isSuperAdmin && selected.status !== 'closed'"
            type="button"
            class="mx-act mx-wa__bot"
            :disabled="memoriaLoading"
            title="Eliminar la memoria del bot en esta conversación"
            @click="eliminarMemoria"
          >
            🧹<span class="mx-wa__botx">{{ memoriaLoading ? 'Eliminando…' : 'Eliminar memoria' }}</span>
          </button>
          <!-- Tipo de usuario (anfitrión ↔ huésped): lo invierte n8n, nosotros
               solo le mandamos el id. Mismas reglas de visibilidad que el de
               memoria. -->
          <button
            v-if="isSuperAdmin && selected.status !== 'closed'"
            type="button"
            class="mx-act mx-wa__bot"
            :disabled="tipoLoading"
            title="Cambiar el tipo de usuario (anfitrión / huésped) en esta conversación"
            @click="cambiarTipo"
          >
            🔄<span class="mx-wa__botx">{{ tipoLoading ? 'Cambiando…' : 'Cambiar tipo' }}</span>
          </button>
          <!-- Recontacto rápido: n8n lo arma, nosotros solo le mandamos el id.
               Solo superadmins (la API también lo valida). -->
          <button
            v-if="isSuperAdmin"
            type="button"
            class="mx-act mx-wa__bot"
            :disabled="recontactoLoading"
            title="Disparar el recontacto rápido de esta conversación"
            @click="recontactoRapido"
          >
            📲<span class="mx-wa__botx">{{ recontactoLoading ? 'Enviando…' : 'Recontactar' }}</span>
          </button>
          <button type="button" class="mx-act" title="Ficha del lead" @click="$emit('toggle-rail')">▤</button>
        </header>

        <div class="mx-log" ref="log" @scroll="onLogScroll">
          <div v-if="loadingMore" class="mx-col__e">Cargando mensajes anteriores…</div>
          <div v-if="loadingMsgs && !messages.length" class="mx-empty">Cargando la conversación…</div>

          <template v-for="(m, i) in decorated">
            <div v-if="m._day" :key="'d' + i" class="mx-day">{{ m._day }}</div>
            <div
              :key="m.id || i"
              class="mx-m"
              :class="[m._side, { 'is-first': m._first, 'is-note': m._note, 'is-new': m._enter, 'is-sending': m._sending }]"
            >
              <div class="mx-b">
                <i v-if="m._note" class="mx-b__tag">Nota interna</i>
                <!-- Quién escribió. Solo en el primero de cada tanda: repetirlo
                     en cada burbuja llena el hilo de ruido. -->
                <i v-else-if="m._first && m._who" class="mx-b__who">{{ m._who }}</i>

                <!-- Multimedia: el backend ya guarda media_url y media_type -->
                <span v-if="m._img" class="mx-b__img">
                  <img :src="m.media_url" :alt="m.body || 'Imagen'" loading="lazy" />
                </span>
                <span v-else-if="m._audio" class="mx-b__audio">
                  <audio :src="m.media_url" controls preload="none"></audio>
                </span>
                <a v-else-if="m.media_url" class="mx-b__doc" :href="m.media_url" target="_blank" rel="noopener" @click.stop>
                  <span class="mx-b__docx">{{ extOf(m.media_url) }}</span>
                  <span class="mx-b__docb"><b>{{ nameOf(m.media_url) }}</b><em>{{ m.media_type || 'archivo' }}</em></span>
                  <span class="mx-b__docg">↓</span>
                </a>

                <!-- El adjunto todavía no volvió del backend: la burbuja ya
                     está puesta, así que dice qué se está subiendo. -->
                <span v-if="m._pendingMedia" class="mx-b__x">Enviando {{ m._pendingMedia }}…</span>

                <!-- v-html porque los links van navegables. El texto lo escribe
                     el cliente, así que linkify() escapa el HTML antes de armar
                     los <a>: ver ui.js. Nunca meter acá nada sin escapar. -->
                <span v-if="m.body" class="mx-b__x" v-html="linkify(m.body)"></span>

                <span class="mx-b__f">
                  {{ hhmm(m.created_at) }}
                  <!-- El ◷ dura lo que tarda el POST: cuando el backend
                       confirma el mensaje, la misma burbuja pasa a ✓✓. -->
                  <span v-if="m._side === 'us' && !m._note" class="mx-b__ck">{{ m._sending ? '◷' : '✓✓' }}</span>
                </span>
              </div>
            </div>
          </template>
        </div>

        <!-- ══ COMPOSITOR ══ -->
        <div v-if="rec" class="mx-comp is-rec">
          <button type="button" class="mx-comp__ico is-kill" title="Descartar" @click="stopRec(false)">✕</button>
          <span class="mx-rec">
            <span class="mx-rec__dot"></span>
            <span class="mx-rec__t">{{ recLabel }}</span>
            <span class="mx-rec__h">grabando…</span>
          </span>
          <button type="button" class="mx-comp__send" title="Enviar el audio" @click="stopRec(true)">➤</button>
        </div>

        <div v-else class="mx-comp" :class="{ 'is-note': isInternal, 'has-text': !!draft.trim() }">
          <div class="mx-comp__clip">
            <button type="button" class="mx-comp__ico" :class="{ 'is-on': clipOpen }" title="Adjuntar" @click.stop="clipOpen = !clipOpen">
              <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21.44 11.05l-9.19 9.19a6 6 0 01-8.49-8.49l9.19-9.19a4 4 0 015.66 5.66l-9.2 9.19a2 2 0 01-2.83-2.83l8.49-8.48"/></svg>
            </button>
            <div v-if="clipOpen" class="mx-clip" @click.stop>
              <button type="button" class="mx-clip__i" @click="pick('img')"><span class="mx-clip__g is-img">▤</span>Fotos y videos</button>
              <button type="button" class="mx-clip__i" @click="pick('doc')"><span class="mx-clip__g is-doc">▧</span>Documento</button>
            </div>
          </div>

          <button
            type="button"
            class="mx-comp__ico"
            :class="{ 'is-on': isInternal }"
            :title="isInternal ? 'Volver a responder por WhatsApp' : 'Escribir una nota interna, no se envía'"
            @click="isInternal = !isInternal"
          >✎</button>

          <textarea
            ref="ta"
            v-model="draft"
            :placeholder="isInternal ? 'Nota interna: no se envía al cliente…' : 'Escribí un mensaje…'"
            @keydown.enter.exact.prevent="send()"
            @input="grow"
          ></textarea>

          <button type="button" class="mx-comp__send mx-comp__mic" title="Grabar un audio" @click="startRec">
            <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 1a3 3 0 00-3 3v8a3 3 0 006 0V4a3 3 0 00-3-3z"/><path d="M19 10v2a7 7 0 01-14 0v-2"/><path d="M12 19v4M8 23h8"/></svg>
          </button>
          <button type="button" class="mx-comp__send mx-comp__go" title="Enviar" :disabled="sending" @click="send()">➤</button>

          <input ref="fimg" type="file" accept="image/*,video/*" multiple hidden @change="onFiles($event, 'img')" />
          <input ref="fdoc" type="file" hidden @change="onFiles($event, 'doc')" />
        </div>
      </template>
    </section>

    <!-- ══ FICHA DEL LEAD ══
         En ancho es la tercera columna. En angosto entra como hoja encima del
         hilo, y ahí necesita una salida a la vista: el velo y la ✕. -->
    <aside v-if="showRail" class="mx-rail">
      <div class="mx-glass"></div>
      <div class="mx-rail__x">
        <span>Ficha del lead</span>
        <button type="button" class="mx-act" title="Cerrar la ficha" aria-label="Cerrar la ficha" @click="$emit('toggle-rail')">✕</button>
      </div>
      <div class="mx-rail__b">
        <div v-if="!selected" class="mx-empty">Sin conversación abierta.</div>

        <template v-else>
          <div class="mx-blk">
            <div class="mx-blk__h">Tiempos</div>
            <div class="mx-blk__b">
              <dl class="mx-kv"><dt>Sin responder</dt><dd :class="waitClass">{{ waitLabel(selected) }}</dd></dl>
              <dl class="mx-kv"><dt>En esta etapa</dt><dd class="is-mono">{{ dur(stageSecs(selected)) }}</dd></dl>
              <dl class="mx-kv"><dt>Primer contacto</dt><dd class="is-mono">{{ selected.first_contact_seconds != null ? dur(selected.first_contact_seconds) : 'todavía no' }}</dd></dl>
            </div>
          </div>

          <div v-if="intake" class="mx-blk">
            <div class="mx-blk__h">Qué busca</div>
            <div class="mx-blk__b">
              <dl class="mx-kv"><dt>Ciudad</dt><dd>{{ intake.ciudad_destino || 'sin dato' }}</dd></dl>
              <dl v-if="intake.zonas_preferidas" class="mx-kv"><dt>Zonas</dt><dd>{{ intake.zonas_preferidas }}</dd></dl>
              <dl class="mx-kv"><dt>Alojamiento</dt><dd>{{ intake.tipo_alojamiento || 'sin definir' }}</dd></dl>
              <dl class="mx-kv"><dt>Presupuesto</dt><dd class="is-mono">{{ intake.presupuesto_usd ? 'USD ' + intake.presupuesto_usd : 'sin dato' }}</dd></dl>
              <dl class="mx-kv"><dt>Personas</dt><dd class="is-mono">{{ intake.cantidad_personas || 1 }}{{ intake.va_en_pareja ? ' · en pareja' : '' }}</dd></dl>
              <dl class="mx-kv"><dt>Estadía</dt><dd class="is-mono">{{ stay }}</dd></dl>
              <dl v-if="intake.universidad_destino" class="mx-kv"><dt>Universidad</dt><dd>{{ intake.universidad_destino }}</dd></dl>
            </div>
          </div>
          <div v-else-if="loadingIntake" class="mx-blk">
            <div class="mx-blk__h">Qué busca</div>
            <div class="mx-blk__b"><p class="mx-meta">Cargando el intake…</p></div>
          </div>
          <div v-else class="mx-blk">
            <div class="mx-blk__h">Qué busca</div>
            <div class="mx-blk__b"><p class="mx-meta">Este lead no tiene un intake cargado, así que no sabemos qué busca.</p></div>
          </div>

          <div class="mx-blk">
            <div class="mx-blk__h">El lead</div>
            <div class="mx-blk__b">
              <dl class="mx-kv">
                <dt>Teléfono</dt>
                <dd class="is-mono">{{ convPhone(selected) }}<span v-if="!phoneComplete(selected)" :title="'Número incompleto'"> ⚠</span></dd>
              </dl>
              <dl class="mx-kv"><dt>Intake</dt><dd class="is-mono">{{ selected.intake_id ? '#' + selected.intake_id : 'sin intake' }}</dd></dl>
              <dl class="mx-kv"><dt>Origen</dt><dd>{{ originLabel(selected) || 'sin dato' }}</dd></dl>
              <dl class="mx-kv"><dt>Sección</dt><dd>{{ sectionLabel }}</dd></dl>
              <dl class="mx-kv"><dt>Asignada a</dt><dd>{{ selected.assigned_agent ? selected.assigned_agent.name : 'sin asignar' }}</dd></dl>
              <dl v-if="intake && intake.email" class="mx-kv"><dt>Correo</dt><dd>{{ intake.email }}</dd></dl>
            </div>
          </div>

          <div v-if="stageReason" class="mx-blk">
            <div class="mx-blk__h">Motivo registrado</div>
            <div class="mx-blk__b">
              <p class="mx-note" style="font-size:12px">{{ stageReason.razon }}</p>
              <p class="mx-meta">{{ stageReason.registrado_por || '' }}</p>
            </div>
          </div>
        </template>
      </div>
    </aside>
    <div v-if="showRail" class="mx-rail-cover" @click="$emit('toggle-rail')"></div>
  </div>
</template>

<script>
/**
 * Mensajería v2 — lista, hilo de WhatsApp y ficha del lead.
 *
 * Los endpoints son los mismos que usa el dashboard actual. El envío de
 * multimedia usa `media_base64`, que el backend ya acepta: no hace falta
 * subir el archivo a ningún lado antes.
 */
import apiUrls from '@/new-constants/apiUrls'
import { ini, dur, waitingSecs, stageSecs, riskOf, convPhone, phoneComplete, originLabel, matches, inDateRange, linkify, authHeaders } from './ui'

export default {
  name: 'ChatV2',
  props: {
    status: { type: String, default: '' },
    sectionLabel: { type: String, default: '' },
    conversationId: { type: [String, Number], default: null },
    listOn: { type: Boolean, default: true },
    railOn: { type: Boolean, default: true },
    /* En angosto la ficha es una hoja encima del hilo y tiene su propio
       interruptor. Los dos los maneja DashboardV2, que es el que sabe si la
       pantalla da para tres columnas. */
    railSheet: { type: Boolean, default: false },
    narrow: { type: Boolean, default: false },
    /* Mismo super admin que usa el tablero (store.getters.isSuperAdmin). Acá
       decide quién ve el botón de borrar la memoria del bot. */
    isSuperAdmin: { type: Boolean, default: false },
    query: { type: String, default: '' },
    origin: { type: String, default: '' },
    /* Rango del último movimiento de sección, tal como lo dejó la barra
       superior. Acá se filtra del lado del front: el endpoint de la lista
       todavía no entiende ?desde/?hasta (solo lo hace el del tablero). */
    desde: { type: String, default: '' },
    hasta: { type: String, default: '' }
  },

  data () {
    return {
      list: [],
      page: 1,
      lastPage: 1,
      loadingList: false,

      selected: null,
      messages: [],
      msgPage: 1,
      msgLastPage: 1,
      loadingMsgs: false,
      loadingMore: false,

      intake: null,
      loadingIntake: false,

      botLoading: false,
      memoriaLoading: false,
      tipoLoading: false,
      recontactoLoading: false,

      editingName: false,
      nameEdit: '',

      draft: '',
      isInternal: false,
      sending: false,
      clipOpen: false,
      rec: null,
      _recTimer: null,

      // Refresco del hilo abierto: ver pollMessages().
      pollTimer: null,
      polling: false,
      // Contador de las burbujas optimistas, para darles una id propia
      // mientras el backend no devolvió la de verdad.
      tmpSeq: 0
    }
  },

  computed: {
    // Sin chat abierto la lista no se puede ocultar: quedaría la nada
    showList () { return this.listOn || !this.selected },
    /* El motivo de etapa, pero solo si lo escribió una persona. Los pases
       automáticos llegan con la traza del workflow ("n8n: primer-contacto/
       Mover stage"), que no le dice nada a quien atiende el chat. */
    stageReason () {
      const r = this.selected && this.selected.stage_reason
      const razon = String((r && r.razon) || '').trim()
      if (!razon || /^n8n\b/i.test(razon)) return null
      return r
    },
    showRail () {
      if (!this.selected) return false
      return this.narrow ? this.railSheet : this.railOn
    },
    /* is-railsheet en vez de is-rail cuando entra como hoja: el CSS necesita
       distinguirlas porque una es una pista de la grilla y la otra flota. */
    railClass () {
      if (!this.showRail) return 'is-norail'
      return this.narrow ? 'is-railsheet' : 'is-rail'
    },

    rows () {
      return this.list.filter(c => {
        if (this.origin && c.lead_origin !== this.origin) return false
        if (!inDateRange(c, this.desde, this.hasta)) return false
        return matches(c, this.query)
      })
    },
    /* El nombre del agente logueado. El backoffice lo guarda en `nombre`, pero
       algunos logins viejos dejaron `name`: se prueban los dos. */
    agentName () {
      return localStorage.getItem('nombre') || localStorage.getItem('name') || null
    },
    /* Con quién se está hablando. Si nadie le puso nombre al contacto todavía,
       el teléfono alcanza para no dejar la burbuja anónima. */
    clientName () {
      if (!this.selected) return 'Cliente'
      return this.selected.client_name || convPhone(this.selected) || 'Cliente'
    },
    /** Mensajes con el lado, el corte de día, quién escribió y el tipo de media ya resueltos. */
    decorated () {
      let lastDay = null
      let lastWho = null
      return this.messages.map(m => {
        const side = this.sideOf(m)
        const day = this.dayOf(m.created_at)
        const note = !!m.is_internal || m.type === 'internal_comment' || m.message_type === 'internal_comment'
        const who = this.whoOf(m, note)
        const out = Object.assign({}, m, {
          _side: side,
          _note: note,
          _day: day !== lastDay ? day : null,
          /* La cabecera con el nombre se repite solo cuando cambia quién
             escribe: en una tanda de cinco mensajes seguidos del mismo agente
             se ve una vez, como en el dashboard viejo. */
          _first: who.key !== lastWho,
          _who: who.label,
          _img: this.isImage(m),
          _audio: this.isAudio(m)
        })
        lastDay = day
        lastWho = who.key
        return out
      })
    },
    botOn () { return !!(this.selected && this.selected.is_bot_active) },
    presence () {
      const w = waitingSecs(this.selected)
      if (w == null) return 'sin actividad'
      return w < 3600 ? 'en línea' : 'últ. vez hace ' + dur(w)
    },
    waitClass () {
      const r = riskOf(this.selected, false)
      return r === 'risk' ? 'is-risk' : r === 'warn' ? 'is-warn' : 'is-mono'
    },
    stay () {
      if (!this.intake) return 'sin dato'
      const a = this.fmtDate(this.intake.fecha_inicio)
      const b = this.fmtDate(this.intake.fecha_fin)
      if (!a && !b) return 'sin dato'
      return (a || '…') + ' → ' + (b || '…')
    },
    recLabel () {
      const s = this.rec ? this.rec.s : 0
      return Math.floor(s / 60) + ':' + ('0' + (s % 60)).slice(-2)
    }
  },

  watch: {
    status: { immediate: true, handler () { this.page = 1; this.loadList() } },
    conversationId: { immediate: true, handler (id) { if (id) this.openById(id) } },
    /* Cambiar de conversación con el nombre a medio editar dejaría el input
       abierto sobre otro lead, y el Enter le pisaría el nombre al que no era. */
    selected () { this.editingName = false; this.nameEdit = '' }
  },

  created () {
    document.addEventListener('click', this.closeClip)
    document.addEventListener('visibilitychange', this.onVisibility)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.closeClip)
    document.removeEventListener('visibilitychange', this.onVisibility)
    this.stopPolling()
    if (this._recTimer) clearInterval(this._recTimer)
  },

  methods: {
    ini, dur, convPhone, phoneComplete, originLabel, riskOf, stageSecs, linkify,
    waitSecs: waitingSecs,
    closeClip () { this.clipOpen = false },

    waitLabel (c) {
      const w = waitingSecs(c)
      return w == null || w <= 0 ? '—' : dur(w)
    },
    /**
     * Quién firma el mensaje. Devuelve la etiqueta que se pinta arriba de la
     * burbuja y una `key` para saber cuándo repetirla: dos mensajes del mismo
     * agente comparten key, uno del bot y uno de una persona no.
     *
     * Los mensajes que entran por WhatsApp suelen venir sin `sender_name`
     * (el webhook solo trae el número), así que del lado del cliente se cae al
     * nombre del contacto de la conversación.
     */
    whoOf (m, note) {
      if (note) return { key: 'internal', label: 'Nota interna' }
      const t = String(m.sender_type || '').toLowerCase()
      if (t === 'bot') return { key: 'bot', label: '🤖 Bot' }
      if (t === 'client') {
        return { key: 'client:' + (m.sender_id || 'x'), label: m.sender_name || this.clientName }
      }
      const name = m.sender_name || 'Agente'
      return { key: 'agent:' + (m.sender_id || name), label: name }
    },
    sideOf (m) {
      if (m.is_internal || m.type === 'internal_comment' || m.message_type === 'internal_comment') return 'us'
      const t = String(m.sender_type || m.direction || '').toLowerCase()
      if (t === 'client' || t === 'in' || t === 'inbound') return 'them'
      return 'us'
    },
    isImage (m) {
      const t = String(m.media_type || '').toLowerCase()
      if (t.indexOf('image') === 0) return true
      return !!m.media_url && /\.(jpe?g|png|gif|webp)(\?|$)/i.test(m.media_url)
    },
    isAudio (m) {
      const t = String(m.media_type || '').toLowerCase()
      if (t.indexOf('audio') === 0) return true
      return !!m.media_url && /\.(ogg|mp3|m4a|wav|opus)(\?|$)/i.test(m.media_url)
    },
    extOf (url) {
      const m = /\.([a-z0-9]{1,5})(\?|$)/i.exec(String(url || ''))
      return m ? m[1].toUpperCase() : 'DOC'
    },
    nameOf (url) {
      const clean = String(url || '').split('?')[0]
      return decodeURIComponent(clean.split('/').pop() || 'archivo')
    },
    hhmm (iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (isNaN(d.getTime())) return ''
      return ('0' + d.getHours()).slice(-2) + ':' + ('0' + d.getMinutes()).slice(-2)
    },
    dayOf (iso) {
      if (!iso) return null
      const d = new Date(iso)
      if (isNaN(d.getTime())) return null
      const hoy = new Date()
      const ayer = new Date(hoy.getTime() - 86400000)
      const same = (a, b) => a.toDateString() === b.toDateString()
      if (same(d, hoy)) return 'Hoy'
      if (same(d, ayer)) return 'Ayer'
      return d.toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })
    },
    fmtDate (v) {
      if (!v) return ''
      const d = new Date(v)
      if (isNaN(d.getTime())) return String(v)
      return ('0' + d.getDate()).slice(-2) + '/' + ('0' + (d.getMonth() + 1)).slice(-2) + '/' + d.getFullYear()
    },
    grow (e) {
      const t = e.target
      t.style.height = 'auto'
      t.style.height = Math.min(130, t.scrollHeight) + 'px'
    },

    // ─── Lista ───────────────────────────────────────────────
    async loadList (append) {
      if (this.loadingList) return
      this.loadingList = true
      try {
        const p = new URLSearchParams({ per_page: '50', page: String(append ? this.page + 1 : 1) })
        if (this.status) p.set('status', this.status)
        const res = await fetch(`${apiUrls.conversationsSupport}?${p}`, { headers: authHeaders() })
        const json = await res.json()
        const rows = (json && json.data) ? json.data : []
        this.list = append ? this.list.concat(rows) : rows
        this.page = append ? this.page + 1 : 1
        this.lastPage = (json && json.meta && json.meta.last_page) || 1
      } catch (e) {
        if (!append) this.list = []
      } finally {
        this.loadingList = false
      }
    },
    onListScroll (e) {
      const el = e.target
      if (this.loadingList || this.page >= this.lastPage) return
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) this.loadList(true)
    },

    // ─── Conversación ────────────────────────────────────────
    open (c) {
      this.selected = c
      this.$emit('open', c.id)
      this.loadMessages(c.id)
      this.loadIntake(c.id)
      this.startPolling()
    },
    /**
     * Volver del hilo a la lista sin salir del chat. Solo hace falta en
     * angosto, donde las dos no caben juntas y la lista queda tapada.
     *
     * Avisa hacia arriba con `open` en null para que openId no quede apuntando
     * a una conversación que ya no está abierta; el watcher del prop ignora el
     * null, así que no reabre nada.
     */
    unopen () {
      this.stopPolling()
      this.selected = null
      this.messages = []
      this.intake = null
      this.clipOpen = false
      if (this.railSheet) this.$emit('toggle-rail')
      this.$emit('open', null)
    },
    async openById (id) {
      if (this.selected && String(this.selected.id) === String(id)) return
      const found = this.list.find(c => String(c.id) === String(id))
      if (found) return this.open(found)
      try {
        const res = await fetch(`${apiUrls.conversationsSupport}/${id}`, { headers: authHeaders() })
        const json = await res.json()
        if (json && json.data) this.open(json.data)
      } catch (e) { /* la conversación ya no existe o no hay permiso */ }
    },
    async loadMessages (id, append) {
      const target = id || (this.selected && this.selected.id)
      if (!target) return
      if (append) this.loadingMore = true; else { this.loadingMsgs = true; this.messages = [] }
      // Los mensajes viejos entran ARRIBA del hilo: sin esto el scroll queda a
      // la misma altura en píxeles y el agente pierde de vista lo que estaba
      // leyendo. Se guarda el alto de antes para devolverlo a su lugar.
      const log = append ? this.$refs.log : null
      const prevHeight = log ? log.scrollHeight : 0
      const prevTop = log ? log.scrollTop : 0
      try {
        const page = append ? this.msgPage + 1 : 1
        const p = new URLSearchParams({ per_page: '50', page: String(page) })
        const res = await fetch(`${apiUrls.conversationsSupport}/${target}/messages?${p}`, { headers: authHeaders() })
        const json = await res.json()
        const rows = (json && json.data) ? json.data.slice() : []
        // El backend devuelve el más nuevo primero; el hilo se lee al revés.
        rows.sort(this.byTime)
        this.messages = append ? rows.concat(this.messages) : rows
        this.msgPage = page
        this.msgLastPage = (json && json.meta && json.meta.last_page) || 1
        if (!append) {
          this.$nextTick(this.toBottom)
        } else {
          this.$nextTick(() => {
            const el = this.$refs.log
            if (el) el.scrollTop = prevTop + (el.scrollHeight - prevHeight)
          })
        }
      } catch (e) {
        if (!append) this.messages = []
      } finally {
        this.loadingMsgs = false
        this.loadingMore = false
      }
    },
    /**
     * Orden del hilo: del más viejo al más nuevo. Los mensajes importados
     * comparten created_at al segundo, así que desempata la id (autoincremental).
     * La burbuja optimista, con id `tmp-N`, va siempre al final: es lo último
     * que se escribió.
     */
    byTime (a, b) {
      const ta = new Date(a.created_at).getTime()
      const tb = new Date(b.created_at).getTime()
      const da = isNaN(ta) ? 0 : ta
      const db = isNaN(tb) ? 0 : tb
      if (da !== db) return da - db

      const ia = Number.isFinite(Number(a.id)) ? Number(a.id) : Infinity
      const ib = Number.isFinite(Number(b.id)) ? Number(b.id) : Infinity
      if (ia === ib) return 0
      return ia < ib ? -1 : 1
    },
    onLogScroll (e) {
      if (e.target.scrollTop < 60 && !this.loadingMore && this.msgPage < this.msgLastPage) {
        this.loadMessages(null, true)
      }
    },
    /* Al abrir una conversación el salto al fondo es instantáneo: animar 50
       burbujas de una es justo el efecto de recarga que había. El `smooth`
       queda para los mensajes que entran de a uno con el hilo ya abierto. */
    toBottom (smooth) {
      const el = this.$refs.log
      if (!el) return
      if (smooth && el.scrollTo) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
      else el.scrollTop = el.scrollHeight
    },
    /* ¿El agente está mirando el final del hilo? Si se fue a leer algo más
       arriba, un mensaje nuevo no le mueve el scroll abajo de los ojos. */
    nearBottom () {
      const el = this.$refs.log
      if (!el) return true
      return el.scrollHeight - el.scrollTop - el.clientHeight < 140
    },

    // ─── Mensajes nuevos sin recargar el hilo ────────────────
    /**
     * El hilo no tiene websocket: lo que contesta el cliente aparece cuando se
     * vuelve a pedir la primera página. Se mergea por id contra lo que ya está
     * en pantalla en vez de reemplazar el array: las burbujas viejas no se
     * vuelven a montar (el hilo no parpadea ni salta) y las nuevas entran con
     * su animación, como el mensaje propio recién enviado.
     *
     * Con la pestaña en segundo plano no se pide nada: son conversaciones
     * abiertas todo el día y no tiene sentido golpear la API sin nadie mirando.
     */
    startPolling () {
      this.stopPolling()
      this.pollTimer = setInterval(this.pollMessages, 10000)
    },
    stopPolling () {
      if (this.pollTimer) { clearInterval(this.pollTimer); this.pollTimer = null }
    },
    /* Volver a la pestaña no espera el próximo tic: se pide en el momento, que
       es justo cuando el agente quiere ver si le contestaron. */
    onVisibility () {
      if (!document.hidden && this.selected) this.pollMessages()
    },
    async pollMessages () {
      const id = this.selected && this.selected.id
      if (!id || document.hidden) return
      // Con una carga en curso el merge llegaría tarde y contra un array que
      // está por cambiar: este tic se saltea y sigue el que viene.
      if (this.polling || this.loadingMsgs || this.loadingMore || this.sending) return
      this.polling = true
      try {
        const p = new URLSearchParams({ per_page: '30', page: '1' })
        const res = await fetch(`${apiUrls.conversationsSupport}/${id}/messages?${p}`, { headers: authHeaders() })
        if (!res.ok) return
        const json = await res.json()
        // Mientras el request volaba el agente pudo cambiar de conversación.
        if (!this.selected || String(this.selected.id) !== String(id)) return
        this.mergeMessages((json && json.data) ? json.data.slice() : [])
      } catch (e) {
        /* un ciclo perdido no es un error que mostrarle al agente */
      } finally {
        this.polling = false
      }
    },
    mergeMessages (rows) {
      rows.sort(this.byTime)
      const known = new Set(this.messages.map(m => String(m.id)))
      const fresh = rows.filter(r => !known.has(String(r.id)))
      if (!fresh.length) return

      // Un envío propio que quedó como burbuja optimista (el POST contestó
      // tarde o sin cuerpo) se descarta acá, o el mensaje se vería dos veces.
      fresh.forEach(r => {
        const i = this.messages.findIndex(m => m._tmp && String(m.body || '') === String(r.body || ''))
        if (i !== -1) this.messages.splice(i, 1)
      })

      const stick = this.nearBottom()
      fresh.forEach(r => this.messages.push(Object.assign({ _enter: true }, r)))
      this.markLastMessage(fresh[fresh.length - 1])
      // Si contestó el cliente cambian los tiempos de la conversación, y de eso
      // viven los conteos de la barra lateral.
      if (fresh.some(r => this.sideOf(r) === 'them')) this.$emit('changed')
      if (stick) this.$nextTick(() => this.toBottom(true))
    },
    /* La lista de la izquierda muestra el último mensaje: sin esto se queda con
       el de antes hasta que se vuelva a pedir la lista entera. */
    markLastMessage (m) {
      if (!m || !this.selected) return
      const preview = m.body || (m.media_url ? 'Archivo adjunto' : '')
      if (!preview) return
      const row = this.list.find(c => String(c.id) === String(this.selected.id))
      if (row) this.$set(row, 'last_message', preview)
    },
    async loadIntake (id) {
      this.intake = null
      this.loadingIntake = true
      try {
        const res = await fetch(`${apiUrls.base}dashboard/intakes/by-conversation/${id}`, { headers: authHeaders() })
        const json = await res.json()
        const list = (json && json.data) ? json.data : (Array.isArray(json) ? json : [])
        if (this.selected && String(this.selected.id) === String(id)) this.intake = list[0] || null
      } catch (e) {
        this.intake = null
      } finally {
        this.loadingIntake = false
      }
    },

    // ─── Nombre del lead ─────────────────────────────────────
    startEditName () {
      if (!this.selected) return
      this.nameEdit = this.selected.client_name || this.selected.name || ''
      this.editingName = true
      this.$nextTick(() => { if (this.$refs.nameInput) this.$refs.nameInput.focus() })
    },
    cancelEditName () {
      this.editingName = false
      this.nameEdit = ''
    },
    /**
     * Guarda el nombre con el mismo PATCH de la conversación que usa el
     * dashboard viejo. Se cierra el input antes de esperar la respuesta: si el
     * PATCH falla se avisa por el toast y el nombre vuelve a verse como estaba,
     * porque el cambio local recién se escribe cuando el backend contesta ok.
     */
    async saveName () {
      if (!this.selected) return
      const id = this.selected.id
      const nuevo = this.nameEdit.trim()
      const previo = this.selected.client_name || this.selected.name || ''
      this.editingName = false
      this.nameEdit = ''
      if (!nuevo || nuevo === previo) return
      try {
        const res = await fetch(`${apiUrls.conversationsSupport}/${id}`, {
          method: 'PATCH',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ client_name: nuevo })
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'No se pudo guardar el nombre.')
        this.markName(id, (json && json.data && json.data.client_name) || nuevo)
      } catch (e) {
        this.$emit('error', e.message)
      }
    },
    markName (id, value) {
      if (this.selected && String(this.selected.id) === String(id)) {
        this.$set(this.selected, 'client_name', value)
      }
      const row = this.list.find(c => String(c.id) === String(id))
      if (row) this.$set(row, 'client_name', value)
    },

    // ─── Bot ─────────────────────────────────────────────────
    /**
     * Prende o apaga el bot de la conversación abierta. Mismo endpoint que usa
     * el dashboard viejo (PATCH .../{id}/bot), así que los dos fronts mueven el
     * mismo interruptor y no hay que tocar nada del backend.
     *
     * El estado se escribe con $set porque la conversación puede venir del
     * endpoint sin la clave `is_bot_active`: sin $set, Vue 2 no la ve cambiar y
     * el botón se queda pintado como estaba.
     */
    async toggleBot () {
      // Solo super admin, igual que el botón del template.
      if (!this.selected || this.botLoading || !this.isSuperAdmin) return
      const id = this.selected.id
      const next = !this.botOn
      this.botLoading = true
      try {
        const res = await fetch(`${apiUrls.conversationsSupport}/${id}/bot`, {
          method: 'PATCH',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify({ is_bot_active: next })
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'No se pudo cambiar el bot.')
        const value = (json && json.data && json.data.is_bot_active != null)
          ? !!json.data.is_bot_active
          : next
        this.markBot(id, value)
      } catch (e) {
        this.$emit('error', e.message)
      } finally {
        this.botLoading = false
      }
    },
    /**
     * Le pide a n8n (vía nuestra API) que borre la memoria del bot de esta
     * conversación: el historial que arrastra como contexto. No cambia nada de
     * lo nuestro, así que no hay que refrescar la conversación ni emitir
     * 'changed'; el resultado se avisa por el toast del dashboard.
     */
    async eliminarMemoria () {
      // El botón ya está escondido para el resto; el guard es para que no se
      // llegue igual por un atajo del template en el futuro.
      if (!this.selected || this.memoriaLoading || !this.isSuperAdmin) return
      const quien = this.selected.client_name || convPhone(this.selected) || 'esta conversación'
      if (!window.confirm(`¿Eliminar la memoria del bot de ${quien}? El bot pierde el contexto de la conversación.`)) return

      this.memoriaLoading = true
      try {
        const res = await fetch(`${apiUrls.conversationsSupport}/${this.selected.id}/eliminar-memoria`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' })
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'No se pudo eliminar la memoria.')
        this.$emit('ok', 'Se borró la memoria del bot en esta conversación.')
      } catch (e) {
        this.$emit('error', e.message)
      } finally {
        this.memoriaLoading = false
      }
    },
    /**
     * Le pide a n8n (vía nuestra API) que cambie el tipo de usuario de esta
     * conversación: anfitrión ↔ huésped. El tipo vive en n8n, así que no hay
     * nada que refrescar acá; el resultado se avisa por el toast.
     */
    async cambiarTipo () {
      if (!this.selected || this.tipoLoading || !this.isSuperAdmin) return
      const quien = this.selected.client_name || convPhone(this.selected) || 'esta conversación'
      if (!window.confirm(`¿Cambiar el tipo de usuario (anfitrión / huésped) de ${quien}?`)) return

      this.tipoLoading = true
      try {
        const res = await fetch(`${apiUrls.conversationsSupport}/${this.selected.id}/cambiar-tipo`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' })
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'No se pudo cambiar el tipo de usuario.')
        // El tipo lo decide el bot (n8n); la API lo lee de su respuesta y
        // arma el mensaje ("Ahora es anfitrión." / "Ahora es huésped.").
        const tipo = json.data && json.data.tipo_usuario
        const etiqueta = tipo === 'anfitrion' ? 'anfitrión' : tipo === 'huesped' ? 'huésped' : null
        if (etiqueta) {
          this.$emit('ok', `${quien} ahora es ${etiqueta}.`)
        } else {
          this.$emit('error', json.message || 'Se cambió el tipo, pero el bot no informó cuál quedó.')
        }
      } catch (e) {
        this.$emit('error', e.message)
      } finally {
        this.tipoLoading = false
      }
    },
    /** Le pide a n8n (vía nuestra API) que dispare el recontacto rápido. */
    async recontactoRapido () {
      if (!this.selected || this.recontactoLoading || !this.isSuperAdmin) return
      const quien = this.selected.client_name || convPhone(this.selected) || 'esta conversación'
      if (!window.confirm(`¿Disparar el recontacto rápido a ${quien}?\n\nConversación #${this.selected.id}`)) return

      this.recontactoLoading = true
      try {
        const res = await fetch(`${apiUrls.conversationsSupport}/${this.selected.id}/recontacto-rapido`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' })
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'No se pudo disparar el recontacto.')
        this.$emit('ok', `Recontacto disparado para ${quien} (conversación #${this.selected.id}).`)
      } catch (e) {
        this.$emit('error', e.message)
      } finally {
        this.recontactoLoading = false
      }
    },
    /* La fila de la lista y la conversación abierta suelen ser el mismo objeto,
       pero no siempre: openById trae una copia del endpoint. Se marcan las dos. */
    markBot (id, value) {
      if (this.selected && String(this.selected.id) === String(id)) {
        this.$set(this.selected, 'is_bot_active', value)
      }
      const row = this.list.find(c => String(c.id) === String(id))
      if (row) this.$set(row, 'is_bot_active', value)
    },

    // ─── Envío ───────────────────────────────────────────────
    /**
     * El POST a /messages solo GUARDA el mensaje: el backend no habla con
     * WhatsApp. El envío real lo dispara el webhook `bot/send-client`, que es
     * el que llega a n8n. Sin esa segunda llamada el mensaje aparece en el hilo
     * pero al cliente no le llega nada — que es lo que pasaba acá.
     *
     * La burbuja se pinta antes de salir el request y se queda en su lugar: lo
     * que devuelve el backend la completa ahí mismo. Antes se volvía a pedir el
     * hilo entero, y como esa carga vacía `messages` y muestra "Cargando la
     * conversación…", cada mensaje enviado parpadeaba el chat completo.
     */
    async post (payload) {
      const id = this.selected && this.selected.id
      if (!id) return
      // Se congelan antes del await: el agente puede tocar el interruptor de
      // nota interna o cambiar de conversación mientras el request vuela.
      const conv = this.selected
      const internal = this.isInternal
      const pending = this.pushPending(payload, internal)
      this.sending = true
      try {
        const res = await fetch(`${apiUrls.conversationsSupport}/${id}/messages`, {
          method: 'POST',
          headers: authHeaders({ 'Content-Type': 'application/json' }),
          body: JSON.stringify(Object.assign({
            is_internal: internal,
            sender_id: Number(localStorage.getItem('idusuario')) || null,
            sender_name: this.agentName,
            message_type: internal ? 'internal_comment' : 'message'
          }, payload))
        })
        const json = await res.json().catch(() => ({}))
        if (!res.ok) throw new Error(json.error || 'No se pudo enviar.')

        // El backend devuelve el mensaje guardado (id y created_at reales): con
        // eso la burbuja que ya está en pantalla queda confirmada, sin pedir
        // nada más.
        this.settlePending(pending, json && json.data)

        // Las notas internas no salen al cliente, así que no se avisa al bot.
        if (!internal) {
          fetch(apiUrls.n8nBotSendClient, {
            method: 'POST',
            headers: authHeaders({ 'Content-Type': 'application/json' }),
            body: JSON.stringify({
              body: payload.body || '',
              client_phone: convPhone(conv) || '',
              conversation_id: id,
              evolution_instance: conv.evolution_instance || null
            })
          }).catch(() => {})
        }

        this.$emit('changed')
      } catch (e) {
        this.dropPending(pending, payload)
        this.$emit('error', e.message)
      } finally {
        this.sending = false
      }
    },
    /**
     * La burbuja optimista: el mensaje tal como se va a ver, con una id
     * temporal hasta que conteste el backend. Se marca `_enter` para que entre
     * con la animación y `_sending` para que muestre el ◷ en vez del ✓✓.
     */
    pushPending (payload, internal) {
      const pending = {
        id: 'tmp-' + (++this.tmpSeq),
        _tmp: true,
        _enter: true,
        _sending: true,
        // El archivo recién existe cuando el backend lo sube, así que mientras
        // tanto la burbuja dice qué se está mandando.
        _pendingMedia: payload.media_base64 ? (payload.media_name || 'archivo') : null,
        conversation_id: this.selected.id,
        sender_type: 'agent',
        sender_id: Number(localStorage.getItem('idusuario')) || null,
        sender_name: this.agentName,
        type: internal ? 'internal_comment' : 'message',
        is_internal: internal,
        body: payload.body || '',
        media_url: null,
        media_type: payload.media_type || null,
        created_at: new Date().toISOString()
      }
      this.messages.push(pending)
      this.$nextTick(() => this.toBottom(true))
      return pending
    },
    /* Reemplaza la temporal por el mensaje real EN SU LUGAR: el nodo es el
       mismo, así que no se vuelve a animar ni salta el scroll. */
    settlePending (pending, data) {
      const i = this.messages.indexOf(pending)
      if (i === -1) return
      const real = Object.assign({}, pending, data || {}, {
        _tmp: false,
        _sending: false,
        _pendingMedia: null
      })
      this.$set(this.messages, i, real)
    },
    /* Si el envío falló la burbuja se va (el mensaje no existe en ningún lado)
       y el texto vuelve al compositor: el error se avisa por el toast, pero
       perder lo escrito obligaría a redactarlo de nuevo. */
    dropPending (pending, payload) {
      const i = this.messages.indexOf(pending)
      if (i !== -1) this.messages.splice(i, 1)
      if (payload.body && !this.draft.trim()) {
        this.draft = payload.body
        this.$nextTick(() => { if (this.$refs.ta) this.$refs.ta.focus() })
      }
    },
    send () {
      const body = this.draft.trim()
      if (!body || this.sending) return
      this.draft = ''
      if (this.$refs.ta) this.$refs.ta.style.height = 'auto'
      this.post({ body })
    },

    pick (kind) {
      this.clipOpen = false
      const el = kind === 'img' ? this.$refs.fimg : this.$refs.fdoc
      if (el) el.click()
    },
    /**
     * El archivo va en base64 en el mismo POST del mensaje: el backend ya lo
     * acepta con `media_base64`, así que no hay que subirlo antes a ningún lado.
     */
    onFiles (e, kind) {
      const files = Array.from(e.target.files || [])
      e.target.value = ''
      if (!files.length) return
      const caption = this.draft.trim()
      this.draft = ''
      files.forEach((f, i) => {
        const reader = new FileReader()
        reader.onload = () => {
          this.post({
            body: i === 0 ? caption : '',
            media_base64: String(reader.result).split(',')[1],
            media_type: f.type || (kind === 'img' ? 'image/jpeg' : 'application/octet-stream'),
            media_name: f.name
          })
        }
        reader.readAsDataURL(f)
      })
    },

    // La grabación real necesita permiso de micrófono; acá va el flujo visible.
    startRec () {
      this.clipOpen = false
      this.rec = { s: 0 }
      this._recTimer = setInterval(() => { if (this.rec) this.rec.s++ }, 1000)
    },
    stopRec (enviar) {
      if (this._recTimer) { clearInterval(this._recTimer); this._recTimer = null }
      const secs = this.rec ? this.rec.s : 0
      this.rec = null
      if (enviar && secs > 0) {
        this.$emit('error', 'La grabación de audio necesita conectar el micrófono. Por ahora se envía como texto.')
        this.post({ body: `🎤 Audio de ${secs}s` })
      }
    }
  }
}
</script>

<style src="./mx.css"></style>
